import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const ModelSelectionBrowser = ({ open, models, onSelect, onClose }) => {
  const [filteredModels, setFilteredModels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    applyFilters();
  }, [models, searchTerm]);

  const applyFilters = () => {
    let filtered = [...models];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        m => m.name.toLowerCase().includes(search) ||
             m.provider.toLowerCase().includes(search) ||
             m.modelId.toLowerCase().includes(search)
      );
    }

    setFilteredModels(filtered);
  };

  const handleSelect = (model) => {
    onSelect(model);
    setSearchTerm('');
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Model</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <TextField
            placeholder="Search by name, provider, or model ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
            }}
          />
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Model Name</TableCell>
                <TableCell>Recommended Temp</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No models found matching your search
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredModels.map((model) => (
                  <TableRow key={model.id} hover>
                    <TableCell>
                      <Chip label={model.provider} size="small" />
                    </TableCell>
                    <TableCell>{model.name}</TableCell>
                    <TableCell>{model.recommendedTemperature ?? 'N/A'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSelect(model)}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

ModelSelectionBrowser.propTypes = {
  open: PropTypes.bool.isRequired,
  models: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ModelSelectionBrowser;
